import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n, useInterval, useLogger, useSelector } from '~Hooks';
import moment from 'moment';
import * as Sentry from '@sentry/browser';
import { handyBreakpoint } from '~Styles/breakpoints';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 0;
  z-index: 9999;
`;

const Banner = styled.div`
  background: #8c2222;
  padding: 10px;
  margin-bottom: 10px;
  font-weight: bold;
  color: var(--colorWhite);
  font-size: 18px;
  box-shadow: 0px 1px 8px rgb(0 0 0 / 30%);
  opacity: 0.7;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);

  ${handyBreakpoint} {
    font-size: 13px;
    margin-bottom: 8px;
    padding: 8px;
  }
`;

const WARN_UNSENT_CUTOFF = 20; // Seconds
const WARN_NO_DATA_CUTOFF = 40; // Seconds

const ConnectionPopover: FC = () => {
  const [unsentEvents, setUnsentEvents] = useState(0);
  const [showDataConnectionWarning, setShowDataConnectionWarning] = useState(false);
  const { i18n } = useI18n();

  const events = useSelector((state) => state.tableEvents);
  const lastTableDataTimestamp = useSelector((state) => state.lastTableEventData);

  const logger = useLogger('connection');
  const isHandy = window.innerWidth <= 600;

  useInterval(() => {
    const now = moment();

    if (!lastTableDataTimestamp) return;

    const lastEvent = events.slice(-1)[0];
    const minutesSinceLastEvent = moment().diff(moment(lastEvent?.createdAt), 'minutes');

    if (minutesSinceLastEvent > 45) {
      setShowDataConnectionWarning(false);
      return;
    }

    // Check if we havent recieved new data in a while
    const dataConnectionBroken = moment(lastTableDataTimestamp)
      .add(WARN_NO_DATA_CUTOFF, 'seconds')
      .isBefore(now);

    // setShowDataConnectionWarning(dataConnectionBroken);

    // Check if we have events that havent synced to backend
    // We do slice -30 so we dont loop every single event which could
    // be thousands
    const filtered = events.slice(-30).filter(
      (e) =>
        e.sentAt === null &&
        moment(e.createdAt)
          .add(WARN_UNSENT_CUTOFF, 'seconds')
          .isBefore(now),
    );

    if (filtered.length > 0) {
      Sentry.captureMessage('POS has unsynced events!');
      logger.warn(`POS has ${filtered.length} unsynced events`);
    }

    if (dataConnectionBroken) {
      // Sentry.captureMessage('POS has no backend connection!');
      // logger.warn(`POS has no backend connection`);
    }

    setUnsentEvents(filtered.length);
  }, 2500);

  if (isHandy) {
    return null;
  }

  return (
    <Wrapper>
      {unsentEvents > 0 && (
        <Banner>
          {i18n('TSR.Warning.OutOfSync')} {unsentEvents}
        </Banner>
      )}
      {showDataConnectionWarning && <Banner>{i18n('TSR.Warning.LostConnection')}</Banner>}
    </Wrapper>
  );
};

export default ConnectionPopover;
